import { faAngleDown, faAngleUp } from "@fortawesome/pro-light-svg-icons";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import {
    faCheck,
    faCircleCheck,
    faCircleExclamation,
    faSparkles,
    faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    useTheme,
} from "@vapor/react-material";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApplyTaxTinderSuggestions } from "../../../../../core/api/use-apply-tax-tinder-suggestions/useApplyTaxTinderSuggestions";
import type { TaxTinderSuggestionGroupDto } from "../../../../../core/api/use-get-tax-tinder-suggestions/TaxTinderSuggestionGroupDto";
import { useGetTaxTinderSuggestions } from "../../../../../core/api/use-get-tax-tinder-suggestions/useGetTaxTinderSuggestions";
import { withFinalPeriod } from "../../../../../utils/stringUtils";
import {
    TAX_TINDER_FAILURE_REASON_KEYS,
    type TaxTinderApplySummary,
    buildApplyTaxTinderSuggestionsPayload,
    makeTaxTinderRowKey,
    summarizeTaxTinderApply,
} from "./utils";

const buildAccordionSx = (theme: ReturnType<typeof useTheme>) => ({
    background: "transparent",
    backgroundImage: "none",
    boxShadow: "none",
    "&:before": { display: "none" },
    "& .MuiAccordionSummary-root, & .MuiAccordionDetails-root": {
        background: "transparent",
    },
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-of-type": { borderBottom: 0 },
    mx: 1,
});

interface TaxTinderDialogProps {
    open: boolean;
    onClose: () => void;
}

export const TaxTinderDialog = ({
    open,
    onClose,
}: TaxTinderDialogProps): ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { data, isFetching: isLoading } = useGetTaxTinderSuggestions();
    const applyTaxTinderSuggestions = useApplyTaxTinderSuggestions();

    const totalTaxPayers = data?.length ?? 0;
    const totalFulfilments =
        data?.reduce(
            (acc, group) => acc + group.suggestedFulfilments.length,
            0,
        ) ?? 0;

    const [expandedDescriptions, setExpandedDescriptions] = useState<
        Set<string>
    >(() => new Set());
    const [uncheckedRows, setUncheckedRows] = useState<Set<string>>(
        () => new Set(),
    );
    const [applySummary, setApplySummary] =
        useState<TaxTinderApplySummary | null>(null);

    const handleAccordionToggle =
        (workspaceId: string) => (_event: unknown, isExpanded: boolean) => {
            setExpandedDescriptions((prev) => {
                const next = new Set(prev);
                if (isExpanded) {
                    next.add(workspaceId);
                } else {
                    next.delete(workspaceId);
                }
                return next;
            });
        };

    const isRowChecked = (rowKey: string) => !uncheckedRows.has(rowKey);

    const setRowChecked = (rowKey: string, checked: boolean) => {
        setUncheckedRows((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    const setManyRowsChecked = (rowKeys: string[], checked: boolean) => {
        setUncheckedRows((prev) => {
            const next = new Set(prev);
            rowKeys.forEach((key) => {
                if (checked) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
            });
            return next;
        });
    };

    const hasCheckedRows =
        data?.some((group) =>
            group.suggestedFulfilments.some((fulfilment) =>
                isRowChecked(
                    makeTaxTinderRowKey(
                        group.workspaceId,
                        fulfilment.levyId,
                        fulfilment.accrualYear,
                    ),
                ),
            ),
        ) ?? false;

    const handleConfirm = async () => {
        if (!data) return;
        const suggestions = buildApplyTaxTinderSuggestionsPayload(
            data,
            isRowChecked,
        );
        if (suggestions.length === 0) {
            onClose();
            return;
        }
        let result;
        try {
            result = await applyTaxTinderSuggestions.mutateAsync({
                suggestions,
            });
        } catch {
            return;
        }
        if (!result) return;
        setApplySummary(
            summarizeTaxTinderApply(data, suggestions, result.failed),
        );
    };

    const handleClose = () => {
        onClose();
    };

    const renderGroupHeaderCheckbox = (group: TaxTinderSuggestionGroupDto) => {
        const groupKeys = group.suggestedFulfilments.map((fulfilment) =>
            makeTaxTinderRowKey(
                group.workspaceId,
                fulfilment.levyId,
                fulfilment.accrualYear,
            ),
        );
        const checkedCount = groupKeys.filter(isRowChecked).length;
        const allChecked = checkedCount === groupKeys.length;
        const indeterminate = checkedCount > 0 && !allChecked;
        return (
            <Checkbox
                checked={allChecked}
                indeterminate={indeterminate}
                onChange={(_e, checked) =>
                    setManyRowsChecked(groupKeys, checked)
                }
            />
        );
    };

    const renderSuccess = (summary: TaxTinderApplySummary) => {
        const isPartial = summary.failedFulfilments > 0;
        // One success is still a success: only a run where everything failed
        // drops the green check for the amber triangle and the counts line.
        const noneApplied = isPartial && summary.fulfilments === 0;
        const resultTitleKey = noneApplied
            ? "views.home.components.tax_tinder.success.none_title"
            : isPartial
              ? "views.home.components.tax_tinder.success.partial_title"
              : "views.home.components.tax_tinder.success.title";
        return (
            <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                <FontAwesomeIcon
                    color={
                        noneApplied
                            ? theme.palette.primary.baseWarningColor
                            : theme.palette.success.main
                    }
                    icon={noneApplied ? faTriangleExclamation : faCircleCheck}
                    size="4x"
                />
                <Typography
                    color={theme.palette.common.black}
                    variant="titleLarge"
                >
                    {t(resultTitleKey)}
                </Typography>
                {!noneApplied && (
                    // Both halves of this line are count-dependent and i18next
                    // resolves one `count` per call, so the tax payers part is
                    // translated first and interpolated as a plain string.
                    <Typography
                        color={theme.palette.primary.textDisabledColor}
                        variant="bodyLarge"
                    >
                        {t(
                            "views.home.components.tax_tinder.success.description",
                            {
                                count: summary.fulfilments,
                                taxPayers: t(
                                    "views.home.components.tax_tinder.success.tax_payers_count",
                                    { count: summary.taxPayers },
                                ),
                            },
                        )}
                    </Typography>
                )}
                {isPartial && (
                    // The Stack resets the Alert's own margins, so the inset lives
                    // on this Box. Everything else (white box, amber border with a
                    // thicker bottom edge, amber icon) comes from Vapor's outlined
                    // warning Alert; only the circle-exclamation glyph is ours.
                    <Box sx={{ alignSelf: "stretch", px: 4 }}>
                        <Alert
                            icon={
                                <FontAwesomeIcon icon={faCircleExclamation} />
                            }
                            severity="warning"
                            variant="outlined"
                        >
                            {/* failedFulfilments is the true failure count: a tuple we cannot name has no bullet but still counts. */}
                            <AlertTitle>
                                <Typography variant="body700">
                                    {t(
                                        "views.home.components.tax_tinder.success.failed_title",
                                        { count: summary.failedFulfilments },
                                    )}
                                </Typography>
                            </AlertTitle>
                            {summary.failedItems.length > 0 && (
                                <ul>
                                    {summary.failedItems.map((item, index) => (
                                        <Typography
                                            key={index}
                                            component="li"
                                            variant="body"
                                        >
                                            {t(
                                                "views.home.components.tax_tinder.success.failed_item",
                                                {
                                                    fulfilmentName:
                                                        item.fulfilmentName,
                                                    taxPayerName:
                                                        item.taxPayerName,
                                                    reason: t(
                                                        TAX_TINDER_FAILURE_REASON_KEYS[
                                                            item.reason
                                                        ],
                                                    ),
                                                },
                                            )}
                                        </Typography>
                                    ))}
                                </ul>
                            )}
                            <Typography variant="body">
                                {t(
                                    "views.home.components.tax_tinder.success.failed_hint",
                                )}
                            </Typography>
                        </Alert>
                    </Box>
                )}
            </Stack>
        );
    };

    const renderLoading = () => (
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography
                color={theme.palette.primary.textTitleColor}
                variant="body"
            >
                {t("views.home.components.tax_tinder.loading")}
            </Typography>
        </Stack>
    );

    const renderSummaryHeader = () => (
        <Stack spacing={0.5} sx={{ px: 2, pt: 1 }}>
            <Typography variant="bodyLarge700">
                {t("views.home.components.tax_tinder.summary.title")}
            </Typography>
            <Typography variant="body">
                {t(
                    "views.home.components.tax_tinder.summary.description.start",
                )}
                <Typography component="span" variant="body700">
                    {t(
                        "views.home.components.tax_tinder.summary.fulfilments_count",
                        { number: totalFulfilments },
                    )}
                </Typography>
                {t(
                    "views.home.components.tax_tinder.summary.description.middle",
                )}
                <Typography component="span" variant="body500">
                    {t(
                        "views.home.components.tax_tinder.summary.taxpayers_count",
                        { number: totalTaxPayers },
                    )}
                </Typography>
                .
            </Typography>
        </Stack>
    );

    const renderGroupAccordion = (group: TaxTinderSuggestionGroupDto) => {
        const isExpanded = expandedDescriptions.has(group.workspaceId);
        return (
            <Accordion
                key={group.workspaceId}
                disableGutters
                elevation={0}
                expanded={isExpanded}
                onChange={handleAccordionToggle(group.workspaceId)}
                sx={buildAccordionSx(theme)}
            >
                <AccordionSummary>
                    <Stack
                        alignItems="center"
                        direction="row"
                        justifyContent="space-between"
                        sx={{ flex: 1 }}
                    >
                        <Stack alignItems="center" direction="row" spacing={1}>
                            <FontAwesomeIcon
                                icon={isExpanded ? faAngleUp : faAngleDown}
                            />
                            <Typography
                                color={
                                    isExpanded
                                        ? theme.palette.primary.textTitleColor
                                        : "primary.interactiveDefault"
                                }
                                sx={{ textDecoration: "underline" }}
                                variant={
                                    isExpanded ? "bodyLarge700" : "subtitle1"
                                }
                            >
                                {group.description}
                            </Typography>
                        </Stack>
                        <Chip
                            icon={
                                <FontAwesomeIcon
                                    color={theme.palette.primary.textTitleColor}
                                    icon={faSparkles}
                                />
                            }
                            label={
                                <Typography
                                    color={theme.palette.primary.textTitleColor}
                                    variant="caption"
                                >
                                    {group.suggestedFulfilments.length}
                                </Typography>
                            }
                            size="small"
                            variant="outlined"
                        />
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    {renderGroupHeaderCheckbox(group)}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body500">
                                        {t(
                                            "views.home.components.tax_tinder.table.column.fulfilment",
                                        )}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body500">
                                        {t(
                                            "views.home.components.tax_tinder.table.column.deadline",
                                        )}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body500">
                                        {t(
                                            "views.home.components.tax_tinder.table.column.reason",
                                        )}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {group.suggestedFulfilments.map((fulfilment) => {
                                const rowKey = makeTaxTinderRowKey(
                                    group.workspaceId,
                                    fulfilment.levyId,
                                    fulfilment.accrualYear,
                                );
                                return (
                                    <TableRow key={rowKey}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isRowChecked(rowKey)}
                                                onChange={(_e, checked) =>
                                                    setRowChecked(
                                                        rowKey,
                                                        checked,
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {fulfilment.fulfilmentName}
                                        </TableCell>
                                        <TableCell>
                                            {fulfilment.deadline}
                                        </TableCell>
                                        <TableCell>
                                            {withFinalPeriod(fulfilment.reason)}
                                            {fulfilment.description && (
                                                <Box>
                                                    {withFinalPeriod(
                                                        fulfilment.description,
                                                    )}
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderResults = () => (
        <Stack spacing={2}>
            {renderSummaryHeader()}
            <Stack>{data?.map(renderGroupAccordion)}</Stack>
        </Stack>
    );

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            open={open}
            PaperProps={{
                sx: { maxHeight: "75%", minHeight: "75%" },
            }}
        >
            <DialogTitle>
                <Stack alignItems="center" direction="row" spacing={1}>
                    <Typography variant="displayMedium">
                        {t("views.home.components.tax_tinder.dialog.title")}
                    </Typography>
                    <Chip
                        icon={
                            <FontAwesomeIcon
                                color={theme.palette.primary.textTitleColor}
                                icon={faSparkles}
                            />
                        }
                        label={
                            <Typography
                                color={theme.palette.primary.textTitleColor}
                                variant="caption"
                            >
                                AI
                            </Typography>
                        }
                        size="small"
                        variant="outlined"
                    />
                </Stack>
                <IconButton color="primary" onClick={handleClose}>
                    <FontAwesomeIcon icon={faClose} />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent style={{ padding: 8 }}>
                {applySummary
                    ? renderSuccess(applySummary)
                    : isLoading
                      ? renderLoading()
                      : renderResults()}
            </DialogContent>
            {!applySummary && (
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">
                        <Typography>
                            {t(
                                "views.home.components.tax_tinder.dialog.cancel",
                            )}
                        </Typography>
                    </Button>
                    <Button
                        disabled={
                            applyTaxTinderSuggestions.isPending ||
                            !hasCheckedRows
                        }
                        onClick={handleConfirm}
                        startIcon={<FontAwesomeIcon icon={faCheck} />}
                        variant="contained"
                    >
                        <Typography>
                            {t(
                                "views.home.components.tax_tinder.dialog.confirm",
                            )}
                        </Typography>
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
